import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Pagination,
  useTheme,
  Paper,
  Container,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Fade,
  alpha,
  Grid,
  Chip,
  Avatar,
} from "@mui/material";

// Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RepeatIcon from "@mui/icons-material/Repeat";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DownloadIcon from "@mui/icons-material/Download";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import Summary from "../../../components/Admin/Common/Summary";
import Filter from "../../../components/Admin/Common/Filter";
import DonorsTable from "../../../components/Admin/Donors/DonorsTable";
import DonorDetailsModal from "../../../components/Admin/Donors/DonorDetailsModal";
import UpdateDonationStatusModal from "../../../components/Admin/Donors/UpdateDonationStatusModal";
import NewMessageModal from "../../../components/Admin/Email/NewMessageModal"; // Import the email modal

// Types & hooks
import { useDonors } from "../../../components/Admin/hooks/useDonors";
import { DonationStatus, EnhancedDonor } from "../../../types/donation";
import { toast } from "react-toastify";

const Donors: React.FC = () => {
  const theme = useTheme();

  // Custom hook for donors
  const {
    donors,
    loading,
    error,
    success,
    donorStats,
    selectedDonor,
    setSelectedDonor,
    donorDonations,
    loadDonors,
    handleViewDonations,
    handleUpdateDonationStatus,
  } = useDonors();

  // Local state for search & pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // 1-based indexing
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal state for updating donation status
  const [updateStatusModal, setUpdateStatusModal] = useState<{
    open: boolean;
    donationId: string | null;
    currentStatus: DonationStatus | null;
    donation?: any;
  }>({ open: false, donationId: null, currentStatus: null, donation: undefined });

  // State for the email modal
  const [isNewMessageOpen, setIsNewMessageOpen] = useState(false);
  const [initialTo, setInitialTo] = useState<string>("");

  // Handlers
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  // For table pagination (zero-based -> adjusting to 1-based)
  const handleTablePageChange = (newPage: number) => {
    setPage(newPage + 1);
  };

  const handleTableRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handlePaginationChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Open the "Update Donation Status" modal
  const openUpdateStatusModal = (
    donationId: string,
    currentStatus: DonationStatus,
    donation: any
  ) => {
    setUpdateStatusModal({
      open: true,
      donationId,
      currentStatus,
      donation,
    });
  };
  const closeUpdateStatusModal = () => {
    setUpdateStatusModal({
      open: false,
      donationId: null,
      currentStatus: null,
      donation: undefined,
    });
  };

  // Actually update the donation status when user confirms
  const handleUpdate = (newStatus: string, reason?: string) => {
    if (updateStatusModal.donationId) {
      handleUpdateDonationStatus(updateStatusModal.donationId, newStatus as any, reason);
      closeUpdateStatusModal();
    }
  };

  // Filter donors by search term
  const filteredDonors = useMemo(() => {
    // Make sure donors is an array before filtering
    const safeDonors = Array.isArray(donors) ? donors : [];
    return safeDonors.filter((donor) =>
      searchTerm
        ? (donor?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          donor?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          donor?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          false)
        : true
    );
  }, [donors, searchTerm]);

  // Transform them into table-ready data - use a simpler approach without type annotations
  const tableData = useMemo(() => {
    if (!filteredDonors || filteredDonors.length === 0) return [];
    
    // This maps the donors to the data format needed for the table
    // without needing to use specific type annotations
    return filteredDonors
      .filter(donor => donor && donor.id)
      .map(donor => ({
      ...donor,
      name: `${donor.firstName || ""} ${donor.lastName || ""}`.trim() || "Unknown",
      donationCount: donor.donationCount || 0,
      totalDonations: donor.totalDonations || 0,
        email: donor.email || "",
    }));
  }, [filteredDonors]);

  // Paginate the table data
  const paginatedData = useMemo(() => {
    if (!tableData || !Array.isArray(tableData) || tableData.length === 0) return [];
    
    const startIndex = (page - 1) * rowsPerPage;
    // Make sure we don't go beyond array bounds
    return tableData.slice(startIndex, Math.min(startIndex + rowsPerPage, tableData.length));
  }, [tableData, page, rowsPerPage]);

  // The total # of pages for our custom pagination
  const totalPages = Math.max(Math.ceil((tableData?.length || 0) / rowsPerPage), 1);

  // Add the missing callbacks for DonorsTable:
  // * onEditDonor
  // * onDeleteDonor
  const handleEditDonor = (donorId: string) => {
   ////console.log("Edit donor:", donorId);
    // e.g., open a modal or route to /donors/edit/:id
  };

  const handleDeleteDonor = (donorId: string) => {
   ////console.log("Delete donor:", donorId);
    // e.g., open confirm dialog or call an API
  };

  // Handler for sending an email to a donor
  const handleSendEmail = (email: string) => {
    setInitialTo(email);
    setIsNewMessageOpen(true);
  };

  // Export donors to CSV
  const handleExportData = () => {
    toast.info("Exporting donors data...");
    // Implementation would go here
  };

  // Loading state if no donors yet
  if (loading && donors.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  // Error state if no donors loaded
  if (error && donors.length === 0) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: "auto", mt: 4 }}>
        <Alert 
          severity="error" 
          variant="filled" 
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={loadDonors} 
          sx={{ 
            mt: 2,
            borderRadius: 2,
            boxShadow: theme.shadows[3],
            px: 3
          }}
          startIcon={<RefreshIcon />}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: theme.palette.mode === 'light' 
          ? alpha(theme.palette.primary.light, 0.04)
          : theme.palette.background.default,
        minHeight: "100vh",
        pb: 6
      }}
    >
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 0, 
          py: 3, 
          px: 4, 
          mb: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`
        }}
      >
        <Container maxWidth="xl">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <VolunteerActivismIcon 
                sx={{ 
                  mr: 2, 
                  fontSize: 40, 
                  color: theme.palette.primary.main 
                }} 
              />
              <div>
                <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                  Donor Management
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Manage donor information and donation history
      </Typography>
              </div>
            </Box>

            <Box display="flex" gap={1}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  onClick={loadDonors} 
                  color="primary"
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Data" arrow>
                <IconButton 
                  onClick={handleExportData}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.success.main, 0.15),
                    }
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help" arrow>
                <IconButton
                  sx={{ 
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.info.main, 0.15),
                    }
                  }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl">
      {/* Error message if donors are loaded but there's still an error */}
      {error && donors.length > 0 && (
          <Alert 
            severity="error" 
            variant="outlined"
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              borderColor: theme.palette.error.main
            }}
          >
          {error}
        </Alert>
      )}

      {/* Success message if any */}
      {success && (
          <Fade in={!!success}>
            <Alert 
              severity="success" 
              variant="filled"
              sx={{ 
                mb: 3, 
                borderRadius: 2,
                boxShadow: theme.shadows[2]
              }}
            >
          {success}
        </Alert>
          </Fade>
      )}

      {/* Summary Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%', 
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1), 
                      color: theme.palette.primary.main,
                      mr: 2
                    }}
                  >
                    <PeopleAltIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Total Donors
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {donorStats.totalDonors.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${donorStats.totalDonors > 0 ? ((donorStats.totalDonors / (donorStats.totalDonors || 1)) * 100).toFixed(0) : 0}% of all users`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                      color: theme.palette.secondary.main,
                      mr: 2
                    }}
                  >
                    <AttachMoneyIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Major Donors
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {donorStats.majorDonors.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${donorStats.totalDonors > 0 ? ((donorStats.majorDonors / (donorStats.totalDonors || 1)) * 100).toFixed(0) : 0}% of total donors`} 
                  size="small" 
                  color="secondary" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              elevation={2} 
              sx={{ 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: alpha(theme.palette.success.main, 0.1), 
                      color: theme.palette.success.main,
                      mr: 2
                    }}
                  >
                    <RepeatIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="subtitle2">
                      Repeat Donors
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {donorStats.repeatDonors.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${donorStats.totalDonors > 0 ? ((donorStats.repeatDonors / (donorStats.totalDonors || 1)) * 100).toFixed(0) : 0}% repeat rate`} 
                  size="small" 
                  color="success" 
                  variant="outlined" 
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Card 
          elevation={2} 
          sx={{ 
            mb: 4, 
            borderRadius: 3,
            overflow: 'visible' 
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Search & Filter
            </Typography>
            <Filter 
              searchValue={searchTerm} 
              onSearchChange={handleSearchChange} 
              loading={loading}
              searchPlaceholder="Search by name or email..." 
            />
          </CardContent>
        </Card>

      {/* Donors Table */}
        <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <DonorsTable
        data={paginatedData}
        loading={loading}
        page={page - 1} // convert to 0-based
        rowsPerPage={rowsPerPage}
        onPageChange={handleTablePageChange}
        onRowsPerPageChange={handleTableRowsPerPageChange}
        onViewDonations={handleViewDonations}
        onEditDonor={handleEditDonor}
        onEmailDonor={handleSendEmail} // Added email action handler
      />

      {/* Custom pagination */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: 2, 
              backgroundColor: alpha(theme.palette.background.paper, 0.6) 
            }}
          >
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePaginationChange}
          color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                }
              }}
            />
          </Box>
        </Card>
      </Container>

      {/* Donor Details Modal */}
      {selectedDonor && (
        <DonorDetailsModal
          open={!!selectedDonor}
          donor={selectedDonor}
          donations={donorDonations.donations}
          loading={loading}
          onClose={() => setSelectedDonor(null)}
          onUpdateStatus={openUpdateStatusModal}
        />
      )}

      {/* Update Donation Status Modal */}
      <UpdateDonationStatusModal
        open={updateStatusModal.open}
        currentStatus={updateStatusModal.currentStatus}
        onClose={closeUpdateStatusModal}
        onUpdate={handleUpdate}
      />

      {/* New Message Modal for sending email to donor */}
      <NewMessageModal
        open={isNewMessageOpen}
        onClose={() => setIsNewMessageOpen(false)}
        onMessageSent={() => {
          toast.success("Email sent successfully.");
          setIsNewMessageOpen(false);
        }}
        initialTo={initialTo}
      />
    </Box>
  );
};

export default Donors;
